import React, { useState, useEffect, useContext } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { logoBackPath } from "../config";
import { NameContext } from "../context/NameContext";
import { useOrbitMenuConfig } from "../hooks/useOrbitMenuConfig";
import { mainMenuItems, aboutSubmenuItems } from "../config/orbitMenuConfig";

const BitterButtonWithMenu = React.forwardRef(({ onMenuClick, onOverlayOpen }, ref) => {
  const [scrollY, setScrollY] = useState(0);
  const [footerOffset, setFooterOffset] = useState(Infinity);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [tooltipCount, setTooltipCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState("main");

  const controls = useAnimation();
  const logoControls = useAnimation();
  const navigate = useNavigate();
  const { name } = useContext(NameContext);

  // Use OrbitMenu configuration
  const { config } = useOrbitMenuConfig();

  const FOOTER_OFFSET_DISTANCE = 50;
  const TOOLTIP_DURATION = config.tooltip.duration;
  const TOOLTIP_REPEAT_DELAY = config.tooltip.repeatDelay;
  const MAX_TOOLTIP_SHOWS = config.tooltip.maxShows;

  useEffect(() => {
    const footer = document.getElementById("footer");
    if (footer) {
      setFooterOffset(footer.offsetTop);
    }

    const tooltipShownCount = parseInt(sessionStorage.getItem('menuTooltipCount') || '0');

    const handleScroll = () => {
      const currentScroll = window.scrollY;
      const viewportHeight = window.innerHeight;
      const maxBottom = footerOffset - FOOTER_OFFSET_DISTANCE;
      const newY = Math.min(currentScroll - [-10 / viewportHeight], maxBottom);
      setScrollY(newY);

      if (currentScroll > 100 && !hasScrolled && tooltipShownCount < MAX_TOOLTIP_SHOWS) {
        setHasScrolled(true);
        showTooltipWithRepeat(tooltipShownCount);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [footerOffset, hasScrolled, tooltipCount]);

  const showTooltipWithRepeat = (currentCount) => {
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
      const newCount = currentCount + 1;
      setTooltipCount(newCount);
      sessionStorage.setItem('menuTooltipCount', newCount.toString());

      if (newCount < MAX_TOOLTIP_SHOWS) {
        setTimeout(() => {
          setShowTooltip(true);
          setTimeout(() => {
            setShowTooltip(false);
            const finalCount = newCount + 1;
            sessionStorage.setItem('menuTooltipCount', finalCount.toString());
          }, TOOLTIP_DURATION);
        }, TOOLTIP_REPEAT_DELAY);
      }
    }, TOOLTIP_DURATION);
  };

  useEffect(() => {
    // Add offset when menu is open to prevent cutoff at top
    const menuOffset = isMenuOpen ? config.visual.menuOffset : 0;
    controls.start({
      top: scrollY + menuOffset,
      transition: {
        type: "spring",
        stiffness: config.animation.scroll.stiffness,
        damping: config.animation.scroll.damping
      },
    });
  }, [scrollY, isMenuOpen, controls, config]);

  useEffect(() => {
    if (isMenuOpen) {
      logoControls.start({
        rotate: config.animation.logo.openRotation,
        transition: {
          type: "spring",
          stiffness: config.animation.logo.stiffness,
          damping: config.animation.logo.damping
        }
      });
    } else {
      logoControls.start({
        rotate: config.animation.logo.closeRotation,
        transition: {
          type: "spring",
          stiffness: config.animation.logo.stiffness,
          damping: config.animation.logo.damping
        }
      });
      setCurrentMenu("main");
    }
  }, [isMenuOpen, logoControls, config]);

  const handleClick = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // Build menu items with dynamic actions
  const buildMenuItems = (items) => {
    return items.map(item => ({
      ...item,
      label: item.dynamic && item.label.includes('{name}')
        ? item.label.replace('{name}', name || 'Demo')
        : item.label,
      onClick: () => {
        // Handle different action types
        if (item.action === 'openOverlay') {
          setIsMenuOpen(false);
          if (onOverlayOpen) onOverlayOpen();
        } else if (item.action === 'openSubmenu') {
          setCurrentMenu(item.submenu);
        } else if (item.action === 'closeSubmenu') {
          setCurrentMenu('main');
        } else if (item.route) {
          navigate(item.route);
          setIsMenuOpen(false);
        }
      }
    }));
  };

  const processedMainMenu = buildMenuItems(mainMenuItems);
  const processedAboutMenu = buildMenuItems(aboutSubmenuItems);

  const radius = config.visual.radius;
  const buttonSize = config.visual.button.width;
  const startAngle = config.visual.startAngle || 0;
  const currentMenuItems = currentMenu === "main" ? processedMainMenu : processedAboutMenu;

  const getPosition = (angle) => {
    const rad = ((angle + startAngle) - 90) * (Math.PI / 180);
    return {
      x: Math.cos(rad) * radius,
      y: Math.sin(rad) * radius
    };
  };

  return (
    <>
      <motion.div
        ref={ref}
        className="fixed right-4 z-[100] mt-28"
        animate={controls}
        initial={{ top: 100 }}
        style={{ bottom: "auto" }}
      >
        {/* Tooltip */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{
            opacity: showTooltip ? 1 : 0,
            x: showTooltip ? 0 : 20
          }}
          transition={{ duration: 0.3 }}
          className="absolute right-16 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ display: showTooltip ? 'block' : 'none' }}
        >
          <div className="bg-gray-900 text-orange-50 text-xs font-mono px-3 py-2 rounded-lg shadow-lg whitespace-nowrap relative">
            Menü öffnen
            <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-gray-900"></div>
          </div>
        </motion.div>

        <motion.div
          className="relative flex items-center justify-center cursor-pointer rounded-full bg-gray-50 border border-gray-200 shadow-lg hover:shadow-gray-500/75"
          style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
          whileHover={{ scale: 1.1 }}
          onClick={handleClick}
        >
          <motion.div
            className="flex items-center justify-center rounded-full overflow-hidden"
            style={{ width: `${buttonSize - 8}px`, height: `${buttonSize - 8}px` }}
            animate={logoControls}
            whileHover={{ rotate: 20, scale: 1.13 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <img
              src={logoBackPath}
              alt="Denis Bitter - Button"
              className="w-15 h-15 rounded-full"
              loading="lazy"
            />
          </motion.div>

          <div className="absolute inset-0 flex items-center justify-center">
            {[...Array(2)].map((_, index) => (
              <motion.div
                key={index}
                className="absolute rounded-full border border-brand"
                style={{ width: "75%", height: "75%" }}
                initial={{ scale: 0.1, opacity: 0.5 }}
                animate={{ scale: 1.5, opacity: 0.4, filter: "blur(0px)" }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "loop",
                  delay: index * 0.5,
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Radial Menu Items - attached to button */}
        <AnimatePresence>
          {isMenuOpen && (
            <div className="absolute top-0 left-0">
              {currentMenuItems.map((item, index) => {
                const position = getPosition(item.angle);

                return (
                  <motion.button
                    key={`${currentMenu}-${item.label}`}
                    initial={{
                      scale: 0,
                      x: 0,
                      y: 0,
                      opacity: 0
                    }}
                    animate={{
                      scale: 1,
                      x: position.x,
                      y: position.y,
                      opacity: 1
                    }}
                    exit={{
                      scale: 0,
                      x: 0,
                      y: 0,
                      opacity: 0
                    }}
                    transition={{
                      type: "spring",
                      stiffness: config.animation.menuItem.stiffness,
                      damping: config.animation.menuItem.damping,
                      delay: index * config.animation.menuItem.staggerDelay
                    }}
                    onClick={item.onClick}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={`absolute rounded-full flex flex-col items-center justify-center cursor-pointer shadow-lg transform ${
                      item.isMainMenu
                        ? "bg-gray-900 text-orange-50 border-2 border-orange-400"
                        : item.isBack
                        ? "bg-orange-600 text-orange-50 border-2 border-orange-400"
                        : "bg-gray-800 text-orange-50 border border-gray-600"
                    } hover:bg-gray-700 transition-colors`}
                    style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
                  >
                    <span className="text-[10px] font-mono text-center leading-tight px-1">
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Backdrop when menu is open */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: config.animation.backdrop.duration }}
            className="fixed inset-0 z-[99]"
            style={{
              backgroundColor: config.visual.colors.backdrop,
              backdropFilter: `blur(${config.visual.backdrop.blur})`,
              WebkitBackdropFilter: `blur(${config.visual.backdrop.blur})`
            }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
});

BitterButtonWithMenu.displayName = 'BitterButtonWithMenu';

export default BitterButtonWithMenu;
